import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'

import { Link } from '~/components'
import { selectUserId } from '~/auth/slices/auth.slice'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { useGetUserSettingsQuery } from '~/redux/apis/user-settings-api'
import { EventDetectionMethod, EventType } from '~/behavior-tracking/types'
import { setSelectedSubjectOrderActionCreator } from '~/redux/slices/dashboard'
import { logUserActivity } from '~/behavior-tracking/utility/view-event-logs.utility'
import LearningPathAvatarIcon from '~/dashboard/components/LearningPathAvatarIcon'
import ResponsiveImage from '~/components/Image/ResponsiveImage'

import { CurriculumTopicDataType } from '../types/curriculum.types'

import { useGetCurriculumLastActivityQuery } from '../apis/get-curriculum-user-stats.api'

import { useGetCurriculumMappingQuizMetadataQuery, useSetGradesMutation } from '../apis/curriculum.api'

import { isMappingQuizApplicable, isMasteryTrackApplicable } from '../utils/validateCountryCodeGrade'
import { useGetCurriculumCountryCodeFromUrlWithoutPrefix } from '../hooks/useGetCurriculumCountryCode'
import {
  selectGradeContent,
  setIsTopicMappingQuizCompleted,
  setCurriculumSubjectContentActionCreator,
} from '../slice/curriculum.slice'

import { curriculumSubjectContentDefault } from '../constants/curriculum.const'

import CurriculumTopicCollapse from './CurriculumTopicCollapse'

export default function SelectSubject({
  subjects,
  quarterNo,
  subjectCounter,
}: {
  subjects: CurriculumTopicDataType[]
  quarterNo: number
  subjectCounter: number
}): React.ReactElement {
  const avatarRef = useRef<HTMLDivElement>(null)
  const [setGrades] = useSetGradesMutation()
  const userId = useAppSelector(selectUserId)
  const router = useRouter()
  const path = router.asPath
  const [, , curriculumLevelId, gradeLevel] = path.split('/')
  const countryCodeFromUrl = useGetCurriculumCountryCodeFromUrlWithoutPrefix()
  const gradeData = useAppSelector(selectGradeContent)
  const mappingQuizApplicable = isMappingQuizApplicable({
    countryCode: countryCodeFromUrl,
    grade: gradeData.gradeLevel.toString(),
  })
  const masteryTrackApplicable = isMasteryTrackApplicable({ countryCode: countryCodeFromUrl })
  const dispatch = useAppDispatch()
  const curriculumSubjectPageUrl = `/curriculum/${path.split('/').slice(2).join('/')}`
  const { data: curriculumMappingQuizMetadata, isLoading } = useGetCurriculumMappingQuizMetadataQuery({
    countryCode: countryCodeFromUrl,
    gradeLevel: gradeData.gradeLevel.toString(),
  })
  const { data: mqOnboardingData } = useGetUserSettingsQuery(undefined, {
    skip: !userId,
  })

  const completedMqOnboarding = mqOnboardingData?.completedMqOnboarding

  useEffect(() => {
    dispatch(setCurriculumSubjectContentActionCreator(curriculumSubjectContentDefault))
  }, [dispatch])

  const lastActivity = useGetCurriculumLastActivityQuery(
    {
      eventName: EventType.CURRICULUM_TOPIC_EVENT,
    },
    {
      skip: !userId,
    },
  ).data

  const onCurriculumSubjectClick = async (
    subject: CurriculumTopicDataType,
    contentItem: boolean = false,
    url: string,
    isMappingQuizDone: boolean,
  ): Promise<void> => {
    dispatch(setIsTopicMappingQuizCompleted(isMappingQuizDone))
    dispatch(setSelectedSubjectOrderActionCreator(subject.subjectOrder))
    // `replaceFullRow` clears the sub-topic on purpose — a freshly picked topic has none yet, and the
    // next activity refills it.
    await setGrades({
      curriculumGrade: Number(gradeLevel),
      subject: subject.subjectLabel,
      subjectOrder: subject.subjectOrder,
      replaceFullRow: true,
    })
    const subjectContent = {
      _id: '',
      subjectLabel: subject.subjectLabel,
      topicId: subject.topicId,
      competencyGoals: subject.competencyGoals,
      subjectOrder: subject.subjectOrder,
      subjectOrderLabel: subject.subjectOrderLabel,
      icon: subject.icon,
      subjectUrl: url,
      isFirstVisit: !completedMqOnboarding,
    }
    if (subjectContent) {
      dispatch(setCurriculumSubjectContentActionCreator(subjectContent))
    }
    if (userId) {
      const extra = {
        title: subject.subjectLabel,
        curriculumLevelId,
        quarterNo,
        subjectOrder: subject.subjectOrder,
        gradeLevel: Number(gradeLevel),
        subdivisionOrder: subject.competencyGoals[0].subdivisionOrder,
      }
      const event = {
        eventName: EventType.CURRICULUM_TOPIC_EVENT,
        viewKey: router.asPath,
        extra: {
          detectBy: contentItem
            ? EventDetectionMethod.ON_CURRICULUM_CONTENT_CLICK
            : EventDetectionMethod.ON_CURRICULUM_TOPIC_CLICK,
          ...extra,
        },
      }

      await logUserActivity(userId, event)
    }
  }

  const getFontSizeClass = (labelCharacterCount: number, masteryCertificatesEarned: boolean | undefined): string => {
    let baseClass = ` text-shadow-md font-bold bg-purple-portal-background bg-clip-text text-center ${
      masteryCertificatesEarned ? ' text-white' : ' text-transparent'
    } `

    baseClass += masteryCertificatesEarned
      ? 'text-xs'
      : labelCharacterCount > 50
      ? 'text-4 leading-4.5 md:text-6 md:leading-7 px-2 tracking-wide2 md:tracking-wide'
      : labelCharacterCount > 30
      ? 'text-md leading-5 md:text-6 md:leading-7 px-2 tracking-normal md:tracking-wider'
      : 'text-md leading-6 md:text-7 md:leading-9 px-6 '

    return baseClass
  }

  return (
    <div className="flex md:flex-row flex-col flex-wrap justify-center gap-16 pt-8 md:pt-16">
      <div className="flex flex-col gap-y-11 md:gap-y-20 md:px-6 max-w-7xl">
        <div
          className="flex flex-wrap flex-flow justify-center gap-9 lg:gap-16"
          key={`${gradeLevel}-${subjects.map((subject) => subject.subjectOrder).join('-')}`}
        >
          {subjects.map((subject) => {
            subjectCounter += 1
            const isLastVisited =
              lastActivity?.curriculumLevelId === curriculumLevelId &&
              lastActivity?.gradeLevel === Number(gradeLevel) &&
              subject.subjectOrder === lastActivity?.subjectOrder &&
              subject.competencyGoals[0].subdivisionOrder === lastActivity?.subdivisionOrder
            const labelCharacterCount = subject.subjectLabel.length
            const url = `${curriculumSubjectPageUrl}/${subject.subjectOrder}`

            const baseId = `${curriculumSubjectPageUrl.split('/').join('#')}#`
            const id = `${baseId}${subject.subjectOrder}`
            const mappingQuizTakenBySubject =
              mappingQuizApplicable &&
              curriculumMappingQuizMetadata?.tasks?.some((task) => task.subjectOrder === subject.subjectOrder)
            const masteryCertificatesEarned = curriculumMappingQuizMetadata?.certificates?.some(
              (certificates) => certificates.subjectOrder === subject.subjectOrder,
            )

            const masteryCertificateSrcKey = `curriculum/certificate/mastery-certificate-${subject.topicId}_v4.png`
            const yetToAchieveCertificateSrcKey = `curriculum/certificate/yet-to-achieve-certificate-${subject.topicId}_v2.png`

            const nextPageUrl = mappingQuizTakenBySubject || !mappingQuizApplicable ? url : `${url}/mapping-quiz`

            const imageClassName = masteryCertificatesEarned
              ? 'flex justify-center items-center w-full h-full mt-2'
              : 'right-1 md:right-2 bottom-1 md:bottom-2 h-8 w-8 md:w-9 md:h-9'

            const shadowClassname = `relative w-full h-18 md:h-30 ${
              mappingQuizTakenBySubject ? 'bg-gradient-neon-green' : 'bg-orange'
            } rounded`

            return (
              <div className="w-69 md:w-83" key={id}>
                <div className="bg-purple rounded w-full text-white">
                  <div className={`w-full rounded bg-white ${masteryCertificatesEarned && 'pb-2.5'}`}>
                    <div className={shadowClassname} id={id}>
                      <div className="absolute -mt-3.5 w-full h-full">
                        <div className="-top-5 left-1/2 absolute flex justify-center items-center bg-purple shadow-md border-2 border-white rounded-full w-8 h-8 -translate-x-1/2 transform">
                          <span className="font-bold text-white">{subject.subjectOrderLabel ?? subjectCounter}</span>
                        </div>

                        <Link
                          className={`flex flex-col items-center rounded shadow-md w-full ${
                            masteryCertificatesEarned
                              ? 'bg-purple-darker h-22 md:h-34 border-white justify-start pt-4 md:pt-6'
                              : 'bg-white h-18 md:h-30 justify-center'
                          }`}
                          onClick={() => {
                            onCurriculumSubjectClick(subject, false, url, mappingQuizTakenBySubject ?? false)
                          }}
                          to={nextPageUrl}
                        >
                          <span className={getFontSizeClass(labelCharacterCount, masteryCertificatesEarned)}>
                            {subject.subjectLabel}
                          </span>
                          {masteryTrackApplicable && !isLoading && (
                            <div className={`absolute ${imageClassName}`}>
                              <ResponsiveImage
                                height={300}
                                imageClassName={`${masteryCertificatesEarned ? 'w-8 md:w-16' : 'w-11'}`}
                                srcKey={
                                  masteryCertificatesEarned ? masteryCertificateSrcKey : yetToAchieveCertificateSrcKey
                                }
                                width={300}
                              />
                            </div>
                          )}
                        </Link>
                        {isLastVisited && (
                          <LearningPathAvatarIcon
                            avatarRef={avatarRef}
                            className="-top-7 md:-top-3 -right-6 md:-right-7"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <CurriculumTopicCollapse
                    mappingQuizTakenBySubject={mappingQuizTakenBySubject ?? false}
                    onItemClick={onCurriculumSubjectClick}
                    subject={subject}
                    subjectUrl={url}
                    url={nextPageUrl}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
